import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Share2, Tag, Phone, ChevronRight } from 'lucide-react';
import { getArticleBySlug, getRelatedArticles } from '@/data/blogArticles';
import ReactMarkdown from 'react-markdown';

const BlogArticle = ({ slug: propSlug, onBack, onArticleClick }) => {
  const params = useParams();
  const navigate = useNavigate();
  const slug = propSlug || params.slug;
  
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  
  const handleBack = onBack || (() => navigate('/blog'));
  const handleArticleClick = onArticleClick || ((newSlug) => navigate(`/blog/${newSlug}`));
  useEffect(() => {
    const foundArticle = getArticleBySlug(slug);
    setArticle(foundArticle);
    
    if (foundArticle) {
      setRelatedArticles(getRelatedArticles(slug, 3));
      
      // Update document title for SEO
      document.title = foundArticle.metaTitle;
      
      // Update meta description
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', foundArticle.metaDescription);
      }
    }
    
    // Scroll to top when article loads
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-bebas text-4xl mb-4">Article Not Found</h1>
          <button 
            onClick={handleBack}
            className="text-[#FF007A] font-medium hover:underline"
          >
            Return to Blog
          </button>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: article.title,
          text: article.metaDescription,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <img 
          src={article.heroImage}
          alt={article.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
        
        {/* Back Button */}
        <button 
          onClick={onBack}
          className="absolute top-24 left-4 md:left-8 flex items-center gap-2 bg-white/10 backdrop-blur-md text-white px-4 py-2 rounded-full hover:bg-white/20 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="font-outfit text-sm">Back to Blog</span>
        </button>

        {/* Article Header */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
          <div className="container mx-auto max-w-4xl">
            <span className="inline-block bg-[#FF007A] text-white text-sm font-bold px-3 py-1 rounded-full mb-4">
              {article.category}
            </span>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-bebas text-3xl md:text-5xl lg:text-6xl text-white mb-4 leading-tight"
            >
              {article.title}
            </motion.h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm font-outfit">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(article.publishDate).toLocaleDateString('en-US', { 
                  month: 'long', 
                  day: 'numeric', 
                  year: 'numeric' 
                })}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {article.readTime}
              </span>
              <button 
                onClick={handleShare}
                className="flex items-center gap-1 hover:text-white transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-[1fr_300px] gap-12 max-w-6xl mx-auto">
            {/* Main Content */}
            <div className="prose prose-lg max-w-none font-outfit
              prose-headings:font-bebas prose-headings:tracking-tight
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:text-[#0A0A0A]
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:text-[#0A0A0A]
              prose-h4:text-lg prose-h4:font-outfit prose-h4:font-bold
              prose-p:text-gray-700 prose-p:leading-relaxed
              prose-a:text-[#FF007A] prose-a:no-underline hover:prose-a:underline
              prose-strong:text-[#0A0A0A]
              prose-ul:list-disc prose-ol:list-decimal
              prose-li:text-gray-700
              prose-table:border-collapse prose-table:w-full
              prose-th:bg-[#0A0A0A] prose-th:text-white prose-th:p-3 prose-th:text-left
              prose-td:border prose-td:border-gray-200 prose-td:p-3
              prose-img:rounded-xl prose-img:shadow-lg
            ">
              <ReactMarkdown>{article.content}</ReactMarkdown>
            </div>

            {/* Sidebar */}
            <aside className="space-y-8">
              {/* Booking CTA */}
              <div className="bg-gradient-to-br from-[#FF007A] to-[#9D00FF] p-6 rounded-2xl text-white sticky top-24">
                <h3 className="font-bebas text-2xl mb-3">READY TO BOOK?</h3>
                <p className="font-outfit text-white/90 text-sm mb-4">
                  Experience the #1 rated day trip from Las Vegas. All-inclusive with breakfast & lunch.
                </p>
                <div className="space-y-3">
                  <a 
                    href="#booking"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.hash = 'booking';
                    }}
                    className="block w-full bg-[#FFD600] text-[#0A0A0A] font-bebas text-center py-3 rounded-xl hover:bg-white transition-colors"
                  >
                    BOOK NOW — $215
                  </a>
                  <a 
                    href="tel:+18777418390"
                    className="flex items-center justify-center gap-2 w-full bg-white/10 text-white font-outfit py-3 rounded-xl hover:bg-white/20 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    1-877-741-8390
                  </a>
                </div>
              </div>

              {/* Tags */}
              <div className="bg-white p-6 rounded-xl border border-gray-200">
                <h3 className="font-bebas text-lg mb-4 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-[#FF007A]" />
                  RELATED TOPICS
                </h3>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full font-outfit"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <div className="bg-white p-6 rounded-xl border border-gray-200">
                  <h3 className="font-bebas text-lg mb-4">RELATED GUIDES</h3>
                  <div className="space-y-4">
                    {relatedArticles.map((related) => (
                      <div 
                        key={related.id}
                        onClick={() => onArticleClick(related.slug)}
                        className="group cursor-pointer"
                      >
                        <h4 className="font-outfit font-medium text-sm group-hover:text-[#FF007A] transition-colors line-clamp-2">
                          {related.title}
                        </h4>
                        <span className="text-xs text-gray-500">{related.readTime}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>
        </div>
      </article>

      {/* Bottom CTA */}
      <section className="bg-[#0A0A0A] py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-bebas text-4xl text-white mb-4">
            TURN ONE DAY INTO A <span className="text-[#FFD600]">MOVIE</span>
          </h2>
          <p className="font-outfit text-white/80 mb-8 max-w-xl mx-auto">
            Join thousands who've experienced the ultimate Las Vegas to Hollywood adventure. Book your spot today.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="#booking"
              onClick={(e) => {
                e.preventDefault();
                window.location.hash = 'booking';
              }}
              className="btn-primary px-8 py-4"
            >
              BOOK YOUR ADVENTURE — $215
            </a>
            <a 
              href="tel:+18777418390"
              className="flex items-center gap-2 text-white hover:text-[#FFD600] transition-colors font-outfit"
            >
              <Phone className="w-5 h-5" />
              1-877-741-8390
            </a>
          </div>
        </div>
      </section>

      {/* More Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-bebas text-2xl">MORE GUIDES YOU'LL LOVE</h2>
              <button 
                onClick={onBack}
                className="text-[#FF007A] font-outfit font-medium flex items-center gap-1 hover:underline"
              >
                View All <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((related) => (
                <div 
                  key={related.id}
                  onClick={() => onArticleClick(related.slug)}
                  className="group cursor-pointer bg-white rounded-xl overflow-hidden border border-gray-200 hover:border-[#FF007A] hover:shadow-lg transition-all"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={related.heroImage}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-[#FF007A] text-xs font-medium">{related.category}</span>
                    <h3 className="font-bebas text-lg mt-1 group-hover:text-[#FF007A] transition-colors line-clamp-2">
                      {related.title}
                    </h3>
                    <div className="flex items-center text-xs text-gray-500 mt-2 gap-3">
                      <span>{related.readTime}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogArticle;
